import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeSlash } from '@gravity-ui/icons';
import { isAuthenticated, setBasicAuth } from '../../services/api';
import { forgotPassword, loginUser, getCurrentUser } from '../../services/auth';
import './LoginPage.css';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (error) setError('');
    if (successMessage) setSuccessMessage('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError('Заполните все поля');
      return;
    }

    setIsSubmitting(true);

    try {
      await loginUser(formData.email, formData.password);

      setBasicAuth(formData.email, formData.password);

      const me = await getCurrentUser();
      const userRole = me.role || 'user';
      localStorage.setItem('user_role', userRole);
      localStorage.setItem('user_name', me.email ? me.email.split('@')[0] : formData.email.split('@')[0]);

      window.dispatchEvent(new Event('auth-change'));
      // Возврат на страницу, с которой увёл RequireAuth (если была).
      const from = location.state?.from?.pathname;
      navigate(from || '/', { replace: true });
    } catch (error) {
      setBasicAuth(null, null);
      localStorage.removeItem('user_role');
      localStorage.removeItem('user_name');

      if (error.response?.status === 401) {
        setError('Неверный email или пароль');
      } else {
        const message =
          error.response?.data?.message ||
          error.response?.data?.detail ||
          'Неверный email или пароль';
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email) {
      setError('Введите email');
      return;
    }

    setIsSubmitting(true);

    try {
      await forgotPassword(formData.email);
      setSuccessMessage('Письмо отправлено на email');
      setError('');
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        'Не удалось отправить письмо';
      setError(message);
      setSuccessMessage('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotToggle = () => {
    setIsForgotMode((prev) => !prev);
    setError('');
    setSuccessMessage('');
  };

  return (
    <div className="login-container auth-page">
      <div className="login-card">
        <form onSubmit={isForgotMode ? handleForgotSubmit : handleSubmit} className="login-form">
          <div className="form-group">
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleInputChange}
              className="login-input"
            />
          </div>

          {!isForgotMode && (
            <div className="form-group password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Пароль"
                value={formData.password}
                onChange={handleInputChange}
                className="login-input"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                title={showPassword ? "Скрыть пароль" : "Показать пароль"}
              >
                {showPassword
                  ? <EyeSlash width={16} height={16} aria-hidden="true" className="eye-icon" />
                  : <Eye width={16} height={16} aria-hidden="true" className="eye-icon" />}
              </button>
            </div>
          )}

          {error && (
            <div className="auth-message auth-message--error">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="auth-message auth-message--success">
              {successMessage}
            </div>
          )}

          <button
            type="submit"
            className="login-btn"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? (isForgotMode ? 'Отправка…' : 'Вход…')
              : (isForgotMode ? 'Отправить письмо' : 'Войти')}
          </button>

          <button type="button" className="login-link-button" onClick={handleForgotToggle}>
            {isForgotMode ? 'Вернуться ко входу' : 'Забыли пароль?'}
          </button>

          <div className="login-register-link">
            Нет аккаунта? <a href="/register">Зарегистрироваться</a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
